import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { exportCSV } from '../utils/exportCSV';

/**
 * Botón "Descargar CSV" reutilizable para las pestañas de análisis.
 *
 * Dos modos, según qué prop llegue:
 *   - `columns`/`rows`: exporta en el NAVEGADOR lo que la pantalla ya tiene
 *     cargado (utils/exportCSV, CSV con BOM UTF-8). Es el modo de siempre, para
 *     tablas de analítica que YA traen el dataset completo (no paginado).
 *   - `onDownload`: delega la descarga completa (filtros + armado del CSV) a
 *     quien use el botón — pensado para listas PAGINADAS como Llamadas, donde
 *     `rows` sería sólo la página visible y "exportar lo filtrado" significa
 *     pedirle al servidor TODO lo que cumple los filtros, no sólo lo que se ve.
 *     El botón sólo se encarga de mostrar el estado de carga.
 *
 * @param {Array<{key: string, label?: string}>} [props.columns]
 * @param {Array<object>} [props.rows]  filas a exportar (dataset visible/filtrado)
 * @param {string} [props.filename]     nombre del archivo (sin necesidad de .csv)
 * @param {string} [props.label='Descargar CSV']
 * @param {() => Promise<void>} [props.onDownload]
 */
export default function DownloadButton({ columns, rows, filename = 'export.csv', label = 'Descargar CSV', onDownload }) {
  const [descargando, setDescargando] = useState(false);
  const modoServidor = !!onDownload;
  const isEmpty = !modoServidor && (!rows || rows.length === 0 || !columns || columns.length === 0);
  const disabled = modoServidor ? descargando : isEmpty;

  const handleClick = async () => {
    if (modoServidor) {
      setDescargando(true);
      try {
        await onDownload();
      } finally {
        setDescargando(false);
      }
      return;
    }
    if (isEmpty) return;
    const name = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    exportCSV({ columns, rows, filename: name });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={isEmpty ? 'Sin datos para exportar' : 'Descargar en CSV'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 6,
        border: '1px solid #2d2d44',
        background: 'rgba(255,255,255,0.03)',
        color: disabled ? '#4b5563' : '#94a3b8',
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      <Download size={14} />
      {descargando ? 'Descargando…' : label}
    </button>
  );
}
